/*
  Active gender for search requests, and for the top menu.

  Product tags are Gender_Mujer and Gender_Hombre. The choice lives in
  sessionStorage (`selectedGender`) and on body[data-gender].
  `women` and `mujer` are one choice. `men` and `hombre` are the other.
  A stored value selects data-collection-mujer or data-collection-hombre
  on every rail, popular and curated. The body value is used when
  nothing is stored.

  A collection link keeps the href from the menu. Nothing here appends
  gender_mujer / gender_hombre, filter.p.tag, or tag:Gender_… to a
  collection path or to the address bar. Those URLs would be extra pages
  for search engines.

  The menu she clicked is the source of truth. A collection link inside
  the Mujer menu stores Mujer, then the browser follows the original href.
  A collection link inside the Hombre menu stores Hombre the same way.
  The previously selected gender does not stay on that click.

  Search still asks Shopify for one gender. The fetch uses
  `tag:Gender_Mujer AND {typed words}` (or Gender_Hombre). The address
  bar and the heading stay the typed words. filter.p.tag is not used: a
  second tag in that filter is OR and widens the grid.

  Popular and curated blocks stay empty until this file fetches one
  collection section. The other collection is never requested.
*/
(function () {
  'use strict';

  var DEFAULT_GENDER = 'mujer';
  var RAIL_SECTION = 'search-gender-rail';

  function currentGender() {
    var stored = '';
    try {
      stored = sessionStorage.getItem('selectedGender') || '';
    } catch (error) {
      stored = '';
    }

    var storedGender = normalizeGenderValue(stored);
    if (storedGender) return storedGender;

    var fromBody = document.body && document.body.getAttribute('data-gender');
    return normalizeGenderValue(fromBody) || DEFAULT_GENDER;
  }

  function tagName() {
    return currentGender() === 'hombre' ? 'Gender_Hombre' : 'Gender_Mujer';
  }

  function normalizeGenderValue(value) {
    var text = String(value || '').trim().toLowerCase();
    if (text === 'hombre' || text === 'men' || text === 'gender_hombre' || text === 'gender-hombre') return 'hombre';
    if (text === 'mujer' || text === 'women' || text === 'gender_mujer' || text === 'gender-mujer') return 'mujer';
    return '';
  }

  function genderFromLabel(value) {
    var text = ' ' + String(value || '').toLowerCase().replace(/[_-]+/g, ' ') + ' ';
    if (/\bhombre\b/.test(text) || /\bmen\b/.test(text)) return 'hombre';
    if (/\bmujer\b/.test(text) || /\bwomen\b/.test(text)) return 'mujer';
    return '';
  }

  function setGender(gender) {
    var next = normalizeGenderValue(gender);
    if (!next) return '';

    try {
      sessionStorage.setItem('selectedGender', next);
      sessionStorage.setItem('visited', 'true');
    } catch (error) {
      /* sessionStorage can be blocked; the body attribute still updates this page */
    }

    if (document.body) {
      document.body.setAttribute('data-gender', next);
      document.body.classList.remove('unvisited');
    }

    return next;
  }

  function strip(query) {
    return String(query || '')
      .replace(/\s+AND\s+tag:Gender_(?:Mujer|Hombre)\b/gi, '')
      .replace(/\btag:Gender_(?:Mujer|Hombre)\s+AND\s+/gi, '')
      .replace(/\btag:Gender_(?:Mujer|Hombre)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function withTag(query) {
    var clean = strip(query);
    var clause = 'tag:' + tagName();
    if (!clean) return clause;
    return clause + ' AND ' + clean;
  }

  function shopifyRoot() {
    var root = window.Shopify && Shopify.routes && Shopify.routes.root;
    if (!root) return '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function safeDecode(value) {
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return value;
    }
  }

  function splitCollection(pathname) {
    var vendor = String(pathname || '').match(
      /^(.*\/collections\/(?:vendors|types)\/[^/]+)(?:\/([^/]+))?\/?$/i
    );
    if (vendor) {
      return { base: vendor[1], tagSegment: vendor[2] || '' };
    }

    var plain = String(pathname || '').match(/^(.*\/collections\/([^/]+))(?:\/([^/]+))?\/?$/i);
    if (!plain) return null;

    var handle = plain[2].toLowerCase();
    if (handle === 'vendors' || handle === 'types') return null;

    return { base: plain[1], tagSegment: plain[3] || '' };
  }

  function normalizeGenderTag(tag) {
    return safeDecode(tag).toLowerCase().replace(/-/g, '_');
  }

  function isGenderTag(tag) {
    var normalized = normalizeGenderTag(tag);
    return normalized === 'gender_mujer' || normalized === 'gender_hombre';
  }

  function stripGenderFromCollectionPath(pathname) {
    var parts = splitCollection(pathname);
    if (!parts || !parts.tagSegment) return pathname;

    var tags = parts.tagSegment.split('+').filter(Boolean);
    var kept = tags.filter(function (tag) {
      return !isGenderTag(tag);
    });

    if (kept.length === tags.length) return pathname;
    if (!kept.length) return parts.base;
    return parts.base + '/' + kept.join('+');
  }

  function stripGenderFilterParams(url) {
    var keys = [];
    url.searchParams.forEach(function (value, key) {
      if (key === 'filter.p.tag' || key.indexOf('filter.p.tag') === 0) keys.push(key);
    });

    keys.forEach(function (key) {
      var values = url.searchParams.getAll(key);
      url.searchParams.delete(key);
      values.forEach(function (value) {
        var kept = value.split(',').filter(function (part) {
          return !isGenderTag(part.trim());
        });
        if (kept.length) url.searchParams.append(key, kept.join(','));
      });
    });
  }

  function publicUrl(urlString) {
    var url;
    try {
      url = new URL(urlString, window.location.origin);
    } catch (error) {
      return urlString;
    }

    if (url.origin !== window.location.origin) return urlString;

    if (/\/collections\//.test(url.pathname)) {
      url.pathname = stripGenderFromCollectionPath(url.pathname);
    }

    if (/\/search\/?$/.test(url.pathname)) {
      var query = url.searchParams.get('q');
      if (query != null) {
        var clean = strip(query);
        if (clean) url.searchParams.set('q', clean);
        else url.searchParams.delete('q');
      }
    }

    stripGenderFilterParams(url);
    return url.pathname + url.search + url.hash;
  }

  function constrainRequestUrl(urlString) {
    var url;
    try {
      url = new URL(urlString, window.location.origin);
    } catch (error) {
      return urlString;
    }

    if (url.origin !== window.location.origin) return urlString;

    if (/\/search\/suggest\/?$/.test(url.pathname)) {
      var types = url.searchParams.get('resources[type]');
      if (!types) {
        url.searchParams.set('resources[type]', 'query,collection,page,article');
        return url.pathname + url.search + url.hash;
      }
      if (/(^|,)product(,|$)/.test(types)) {
        var nextTypes = types.split(',').filter(function (type) {
          return type && type !== 'product';
        });
        url.searchParams.set('resources[type]', nextTypes.length ? nextTypes.join(',') : 'query');
        return url.pathname + url.search + url.hash;
      }
      return urlString;
    }

    /*
      Search fetches include the gender tag so Shopify returns one gender.
      This URL is not written to the address bar.
    */
    if (/\/search\/?$/.test(url.pathname)) {
      var query = url.searchParams.get('q');
      if (query == null || strip(query) === '') return urlString;
      var nextQuery = withTag(query);
      if (nextQuery === query.replace(/\s+/g, ' ').trim()) return urlString;
      url.searchParams.set('q', nextQuery);
      return url.pathname + url.search + url.hash;
    }

    return urlString;
  }

  function installFetchPatch() {
    if (window.fetch && window.fetch.__genderProducts) return;

    var originalFetch = window.fetch;
    if (typeof originalFetch !== 'function') return;

    var patched = function (input, init) {
      try {
        if (typeof input === 'string') {
          input = constrainRequestUrl(input);
        } else if (input && typeof input.url === 'string') {
          var next = constrainRequestUrl(input.url);
          if (next !== input.url) input = new Request(next, input);
        }
      } catch (error) {
        /* keep the original request */
      }

      return originalFetch.call(this, input, init);
    };

    patched.__genderProducts = true;
    window.fetch = patched;
  }

  function installHistoryPatch() {
    if (!window.history || (history.pushState && history.pushState.__genderProducts)) return;

    ['pushState', 'replaceState'].forEach(function (name) {
      var original = history[name];
      if (typeof original !== 'function') return;

      var wrapped = function (state, title, url) {
        if (typeof url === 'string') url = publicUrl(url);
        return original.call(history, state, title, url);
      };

      wrapped.__genderProducts = true;
      history[name] = wrapped;
    });
  }

  function cleanSearchFields(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('input[name="q"]').forEach(function (input) {
      if (document.activeElement === input) return;
      var clean = strip(input.value);
      if (clean !== input.value) input.value = clean;
    });
  }

  function cleanSearchHrefs(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('a[href]').forEach(function (link) {
      var url;
      try {
        url = new URL(link.href, window.location.origin);
      } catch (error) {
        return;
      }
      if (url.origin !== window.location.origin) return;
      var next = publicUrl(url.href);
      var current = url.pathname + url.search + url.hash;
      if (next !== current) link.href = next;
    });
  }

  function onSearchSubmit(event) {
    var form = event.target;
    if (!form || !form.getAttribute || form.getAttribute('role') !== 'search') return;
    if (event.defaultPrevented) return;

    var input = form.querySelector('input[name="q"]');
    if (!input) return;

    var clean = strip(input.value);
    if (input.value !== clean) input.value = clean;
  }

  function triggerForDrawer(drawer) {
    if (!drawer || !drawer.id) return null;
    var triggers = document.querySelectorAll('[data-megamenu-target]');
    for (var i = 0; i < triggers.length; i++) {
      if (triggers[i].getAttribute('data-megamenu-target') === drawer.id) return triggers[i];
    }
    return null;
  }

  function menuGender(link) {
    if (!link || !link.closest) return '';

    var drawer = link.closest('.megamenu-drawer');
    if (drawer) {
      var trigger = triggerForDrawer(drawer);
      if (trigger) {
        var fromTrigger =
          normalizeGenderValue(trigger.getAttribute('data-select-gender')) ||
          genderFromLabel(trigger.getAttribute('data-megamenu-target')) ||
          genderFromLabel(trigger.textContent);
        if (fromTrigger) return fromTrigger;
      }
      var fromDrawer = genderFromLabel(drawer.id);
      if (fromDrawer) return fromDrawer;
    }

    var root = link.closest('.header__inline-menu, header-drawer, .menu-drawer, #menu-drawer, .header, .megamenu-drawer');
    if (root) {
      var item = link.closest('li');
      var top = item;
      while (item && root.contains(item)) {
        var parentList = item.parentElement;
        var parentItem = parentList && parentList.closest ? parentList.closest('li') : null;
        if (!parentItem || !root.contains(parentItem)) break;
        top = parentItem;
        item = parentItem;
      }
      if (top) {
        var labelNode = top.querySelector(
          ':scope > a, :scope > summary, :scope > button, :scope > details > summary, :scope > header-menu > details > summary'
        );
        var fromLabel = genderFromLabel(labelNode ? labelNode.textContent : '');
        if (fromLabel) return fromLabel;
      }
    }

    var marked = link.closest('[data-search-gender], [data-menu-gender], [data-select-gender]');
    if (marked) {
      var raw =
        marked.getAttribute('data-search-gender') ||
        marked.getAttribute('data-menu-gender') ||
        marked.getAttribute('data-select-gender');
      var fromMark = normalizeGenderValue(raw) || genderFromLabel(raw);
      if (fromMark) return fromMark;
    }

    return '';
  }

  function onMenuCollectionClick(event) {
    if (event.defaultPrevented) return;
    if (event.type === 'click' && event.button !== 0) return;

    var link = event.target && event.target.closest && event.target.closest('a[href]');
    if (!link) return;

    var url;
    try {
      url = new URL(link.href, window.location.origin);
    } catch (error) {
      return;
    }

    if (url.origin !== window.location.origin) return;
    if (!/\/collections\//.test(url.pathname)) return;

    var gender = menuGender(link);
    if (!gender) return;

    setGender(gender);
  }

  function replaceOnce(nextUrl) {
    try {
      if (sessionStorage.getItem('cosGenderRedirect') === nextUrl) return;
      sessionStorage.setItem('cosGenderRedirect', nextUrl);
    } catch (error) {
      /* still navigate once */
    }

    if (nextUrl === window.location.pathname + window.location.search + window.location.hash) return;
    window.location.replace(nextUrl);
  }

  function cleanAddressBar() {
    var next = publicUrl(window.location.href);
    var current = window.location.pathname + window.location.search + window.location.hash;
    if (next === current) return false;

    if (/\/collections\//.test(window.location.pathname) && stripGenderFromCollectionPath(window.location.pathname) !== window.location.pathname) {
      replaceOnce(next);
      return true;
    }

    window.history.replaceState(window.history.state, '', next);
    return false;
  }

  function isSearchPage() {
    return /\/search\/?$/.test(window.location.pathname);
  }

  function searchSectionId() {
    var grid = document.getElementById('product-grid');
    if (grid && grid.dataset.id) return grid.dataset.id;

    var mount = document.querySelector('[data-gender-search-mount]');
    if (mount && mount.getAttribute('data-section-id')) return mount.getAttribute('data-section-id');

    var shell = document.querySelector('.template-search');
    var section = shell && shell.closest('[id^="shopify-section-"]');
    return section ? section.id.replace('shopify-section-', '') : '';
  }

  function searchTagMatchesPage() {
    var params = new URLSearchParams(window.location.search);
    var match = /tag:Gender_(Mujer|Hombre)/i.exec(params.get('q') || '');
    if (!match) return false;
    if (!document.getElementById('product-grid')) return false;
    return match[1].toLowerCase() === currentGender();
  }

  function afterSearchHtml(section) {
    cleanSearchFields(section);
    cleanSearchHrefs(section);
    loadVisibleRails(section);

    if (window.SearchGender && typeof SearchGender.setupScope === 'function') {
      section.querySelectorAll('predictive-search, search-form').forEach(function (scope) {
        SearchGender.setupScope(scope);
      });
    }
  }

  function refreshSearchPage() {
    if (!isSearchPage()) return;

    var params = new URLSearchParams(window.location.search);
    var clean = strip(params.get('q') || '');
    if (!clean) return;

    var alreadyFiltered = searchTagMatchesPage();
    cleanAddressBar();

    if (alreadyFiltered) {
      var existingId = searchSectionId();
      var existing = existingId && document.getElementById('shopify-section-' + existingId);
      if (existing) afterSearchHtml(existing);
      return;
    }

    var id = searchSectionId();
    if (!id) return;

    var fetchUrl = new URL(window.location.href);
    fetchUrl.searchParams.set('q', withTag(clean));
    fetchUrl.searchParams.set('section_id', id);

    var requestId = String(Date.now()) + Math.random().toString(16).slice(2);
    refreshSearchPage.current = requestId;

    window.fetch(fetchUrl.pathname + fetchUrl.search, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then(function (html) {
        if (refreshSearchPage.current !== requestId) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.getElementById('shopify-section-' + id);
        var current = document.getElementById('shopify-section-' + id);
        if (!fresh || !current) return;
        current.innerHTML = fresh.innerHTML;
        afterSearchHtml(current);
      })
      .catch(function () {
        if (refreshSearchPage.current !== requestId) return;
        var mount = document.querySelector('[data-gender-search-mount]');
        if (mount) mount.textContent = 'No se pudieron cargar los resultados.';
      });
  }

  function attributeHandle(rail, gender) {
    return String(rail.getAttribute('data-collection-' + gender) || '').trim();
  }

  /*
    Each rail has its own pair of handles. Popular and curated both
    follow the stored gender. The rail stays visible while the
    collection section loads. A failed request does not hide it.
  */
  function collectionHandle(rail) {
    var gender = currentGender();
    var handle = attributeHandle(rail, gender);
    if (handle) return handle;
    if (gender === 'mujer') return '';
    return attributeHandle(rail, 'mujer');
  }

  function loadRail(rail, explicitHandle) {
    /*
      forEach(loadRail) passes the list index as the second argument.
      That index is not a collection handle. Only a string is.
    */
    var handle = typeof explicitHandle === 'string' && explicitHandle
      ? explicitHandle
      : collectionHandle(rail);
    var list = rail.querySelector('[data-scroll-list]');
    if (!list) return;

    if (!handle) return;

    rail.hidden = false;

    if (rail.getAttribute('data-loaded') === handle && list.children.length > 0) return;

    var requestId = String(Date.now()) + Math.random().toString(16).slice(2);
    rail.setAttribute('data-request', requestId);

    var url = shopifyRoot() + 'collections/' + encodeURIComponent(handle) + '?section_id=' + RAIL_SECTION;

    window.fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then(function (html) {
        if (rail.getAttribute('data-request') !== requestId) return;

        var railId = rail.getAttribute('data-rail-id') || handle;
        var markup = html.split('__RAIL_ID__').join(railId);
        var parsed = new DOMParser().parseFromString(markup, 'text/html');
        var source = parsed.querySelector('[data-gender-rail-results]');
        var nextItems = [];

        if (source) {
          Array.prototype.forEach.call(source.querySelectorAll('li'), function (item) {
            nextItems.push(document.importNode(item, true));
          });
        }

        if (!nextItems.length) return;

        list.innerHTML = '';
        nextItems.forEach(function (item) {
          list.appendChild(item);
        });
        rail.setAttribute('data-loaded', handle);
        rail.hidden = false;
      })
      .catch(function () {
        if (rail.getAttribute('data-request') !== requestId) return;
        rail.hidden = false;
      });
  }

  function railIsDeferred(rail) {
    var details = rail.closest('details');
    return !!(details && !details.open);
  }

  function loadVisibleRails(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-gender-rail]').forEach(function (rail) {
      if (railIsDeferred(rail)) return;
      loadRail(rail);
    });
  }

  function onGenderChange() {
    if (isSearchPage()) refreshSearchPage();
    loadVisibleRails();
    document.querySelectorAll('predictive-search').forEach(function (element) {
      if (typeof element.updateGenderRecommendations === 'function') element.updateGenderRecommendations();
      if (typeof element.refreshGenderResults === 'function') element.refreshGenderResults();
    });
  }

  function bindRails() {
    document.addEventListener(
      'toggle',
      function (event) {
        var details = event.target;
        if (!details || !details.open || !details.querySelectorAll) return;
        details.querySelectorAll('[data-gender-rail]').forEach(function (rail) {
          loadRail(rail);
        });
      },
      true
    );
  }

  function init() {
    installFetchPatch();
    installHistoryPatch();
    cleanSearchFields(document);
    cleanSearchHrefs(document);
    if (!isSearchPage() && cleanAddressBar()) return;
    if (isSearchPage()) refreshSearchPage();
    loadVisibleRails();
    bindRails();

    if (document.body) {
      new MutationObserver(onGenderChange).observe(document.body, {
        attributes: true,
        attributeFilter: ['data-gender']
      });
    }
  }

  installFetchPatch();
  installHistoryPatch();
  document.addEventListener('submit', onSearchSubmit, false);
  document.addEventListener('click', onMenuCollectionClick, true);
  document.addEventListener('auxclick', onMenuCollectionClick, true);

  window.GenderProducts = {
    currentGender: currentGender,
    collectionHandle: collectionHandle,
    tagName: tagName,
    setGender: setGender,
    menuGender: menuGender,
    strip: strip,
    withTag: withTag,
    publicUrl: publicUrl,
    constrainRequestUrl: constrainRequestUrl,
    stripGenderFromCollectionPath: stripGenderFromCollectionPath,
    loadVisibleRails: loadVisibleRails,
    refreshSearchPage: refreshSearchPage,
    onGenderChange: onGenderChange
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
